import { useState, useEffect } from 'react';
import { ARAC_TIPLERI, ARAC_DURUMLARI } from '@/lib/constants';

interface Vehicle {
  id?: number;
  ad: string;
  plaka: string;
  marka_model: string;
  arac_tipi: string;
  sofor: string;
  kapasite: number;
  durum: string;
  gunluk_gorev_sayisi: number;
}

interface Props {
  vehicle?: Vehicle | null;
  onSave: (data: Omit<Vehicle, 'id'>) => Promise<void>;
  onClose: () => void;
  saving: boolean;
}

const EMPTY: Omit<Vehicle, 'id'> = {
  ad: '', plaka: '', marka_model: '', arac_tipi: 'Mercedes Vito',
  sofor: '', kapasite: 4, durum: 'Müsait', gunluk_gorev_sayisi: 0,
};

export default function AracModal({ vehicle, onSave, onClose, saving }: Props) {
  const [form, setForm] = useState<Omit<Vehicle, 'id'>>(EMPTY);

  useEffect(() => {
    if (vehicle) {
      const { id: _id, ...rest } = vehicle as Vehicle;
      setForm({ ...EMPTY, ...rest });
    } else {
      setForm(EMPTY);
    }
  }, [vehicle]);

  const set = (key: keyof typeof EMPTY, value: string | number) =>
    setForm(prev => ({ ...prev, [key]: value }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.ad.trim() || !form.plaka.trim()) return;
    await onSave(form);
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-vip-dark border border-vip-gold/15 rounded-xl w-full max-w-lg my-4">
        <div className="flex items-center justify-between p-5 border-b border-vip-gold/10">
          <h3 className="text-base font-semibold text-white">{vehicle ? 'Aracı Düzenle' : 'Yeni Araç Ekle'}</h3>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-md hover:bg-vip-gold/10 text-gray-400 hover:text-white transition-colors">
            <i className="ri-close-line"></i>
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs text-gray-400 mb-1.5">Araç Adı *</label>
              <input value={form.ad} onChange={e => set('ad', e.target.value)} required
                placeholder="VIP-01" className="w-full bg-vip-black border border-vip-gold/15 rounded-md px-3 py-2.5 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-vip-gold/40" />
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1.5">Plaka *</label>
              <input value={form.plaka} onChange={e => set('plaka', e.target.value)} required
                placeholder="07 ABC 123" className="w-full bg-vip-black border border-vip-gold/15 rounded-md px-3 py-2.5 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-vip-gold/40 font-mono" />
            </div>
          </div>
          <div>
            <label className="block text-xs text-gray-400 mb-1.5">Marka / Model</label>
            <input value={form.marka_model} onChange={e => set('marka_model', e.target.value)}
              placeholder="Mercedes-Benz Vito 119" className="w-full bg-vip-black border border-vip-gold/15 rounded-md px-3 py-2.5 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-vip-gold/40" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs text-gray-400 mb-1.5">Araç Tipi</label>
              <select value={form.arac_tipi} onChange={e => set('arac_tipi', e.target.value)}
                className="w-full bg-vip-black border border-vip-gold/15 rounded-md px-3 py-2.5 text-sm text-white focus:outline-none focus:border-vip-gold/40">
                {ARAC_TIPLERI.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1.5">Kapasite (Kişi)</label>
              <input type="number" min={1} max={50} value={form.kapasite} onChange={e => set('kapasite', Number(e.target.value))}
                className="w-full bg-vip-black border border-vip-gold/15 rounded-md px-3 py-2.5 text-sm text-white focus:outline-none focus:border-vip-gold/40" />
            </div>
          </div>
          <div>
            <label className="block text-xs text-gray-400 mb-1.5">Şoför Adı</label>
            <input value={form.sofor} onChange={e => set('sofor', e.target.value)}
              placeholder="Mehmet Kaya" className="w-full bg-vip-black border border-vip-gold/15 rounded-md px-3 py-2.5 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-vip-gold/40" />
          </div>
          <div>
            <label className="block text-xs text-gray-400 mb-1.5">Durum</label>
            <div className="flex gap-2">
              {ARAC_DURUMLARI.map(d => (
                <button key={d} type="button" onClick={() => set('durum', d)}
                  className={`flex-1 py-2 rounded-md text-xs font-medium transition-all border ${form.durum === d ? 'bg-vip-gold/15 text-vip-gold border-vip-gold/30' : 'bg-vip-black text-gray-400 border-vip-gold/10 hover:text-white'}`}>
                  {d}
                </button>
              ))}
            </div>
          </div>
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="flex-1 py-2.5 rounded-md bg-vip-black border border-vip-gold/10 text-gray-400 text-sm hover:text-white transition-colors whitespace-nowrap">İptal</button>
            <button type="submit" disabled={saving || !form.ad.trim() || !form.plaka.trim()}
              className="flex-1 py-2.5 rounded-md bg-vip-gold text-vip-black font-semibold text-sm hover:bg-vip-gold-light transition-colors disabled:opacity-50 whitespace-nowrap">
              {saving ? 'Kaydediliyor...' : vehicle ? 'Güncelle' : 'Ekle'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}